import { useEffect, useRef, useState } from "react";
import {
  fetchAssociation,
  associateThread,
  removeAssociation,
  searchDeals,
  type ThreadAssociation,
  type DealSearchHit,
} from "../../api";

interface Props {
  /** The opened email's Graph conversationId. The whole thread links as one. */
  conversationId: string;
  /** Fired after any link change so parents can refetch deal-scoped views. */
  onChanged?: () => void;
  /** EM-6: report the currently-linked deal (or null) so the host modal can
   *  auto-scope deal-scoped actions (Save-to-WorkDrive / From-docs). */
  onLinked?: (deal: { id: string; name: string } | null) => void;
}

/**
 * Association banner (Piece 2, Stage 4) — shows on every opened email (shared
 * across the feed, the workspace, and the per-deal tab via EmailDetailModal).
 *
 * Shows the thread's current deal link (auto-matched or manually pinned), and
 * lets the user pin it to an active deal, search closed deals (escrow/closing
 * mail), correct a wrong link, or remove a manual one. A manual link is
 * authoritative server-side — it overrides auto-matching everywhere and sticks
 * to the whole conversation. This component is layout + the link mutations only.
 */
export function EmailAssociationBanner({ conversationId, onChanged, onLinked }: Props) {
  const [assoc, setAssoc] = useState<ThreadAssociation | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search state for the picker. Closed/funded deals are INCLUDED by default —
  // escrow/closing mail routinely lands after a deal leaves active stage, so
  // hiding them behind a toggle was the wrong default. Uncheck to narrow to
  // active-only.
  const [query, setQuery] = useState("");
  const [includeClosed, setIncludeClosed] = useState(true);
  const [hits, setHits] = useState<DealSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const reqRef = useRef(0);

  const load = async () => {
    if (!conversationId) return;
    try {
      setAssoc(await fetchAssociation(conversationId));
    } catch {
      setAssoc({ linked: false });
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // EM-6: report the linked deal (or null) up whenever the association resolves
  // or changes, so the host modal can auto-scope deal actions.
  useEffect(() => {
    if (!assoc) return;
    onLinked?.(
      assoc.linked && assoc.deal_id
        ? { id: assoc.deal_id, name: assoc.deal_name ?? assoc.deal_id }
        : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assoc]);

  // Debounced deal search (stale-response guarded, mirrors the workspace search).
  useEffect(() => {
    if (!picking) return;
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const myReq = ++reqRef.current;
    const t = setTimeout(async () => {
      try {
        const { deals } = await searchDeals(q, includeClosed);
        if (myReq === reqRef.current) setHits(deals);
      } catch {
        if (myReq === reqRef.current) setHits([]);
      } finally {
        if (myReq === reqRef.current) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, includeClosed, picking]);

  const pin = async (hit: DealSearchHit) => {
    setBusy(true);
    setError(null);
    try {
      await associateThread({
        conversation_id: conversationId,
        deal_id: hit.id,
        deal_name: hit.name,
      });
      setPicking(false);
      setQuery("");
      setHits([]);
      await load();
      onChanged?.();
    } catch {
      setError("Couldn’t link this thread. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    setError(null);
    try {
      await removeAssociation(conversationId);
      await load();
      onChanged?.();
    } catch {
      setError("Couldn’t remove the link. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!conversationId) return null;

  const linked = assoc?.linked;
  const isManual = assoc?.source === "manual";

  return (
    <div className="email-assoc">
      <div className="email-assoc-row">
        <span className="email-assoc-status">
          {!assoc ? (
            <span className="email-assoc-dim">Checking deal link…</span>
          ) : linked ? (
            <>
              <span className={isManual ? "email-assoc-pin" : "email-assoc-auto"}>
                {isManual ? "📌 Linked" : "🔗 Auto-matched"}
              </span>{" "}
              to <b>{assoc.deal_name || "a deal"}</b>
              {!isManual && <span className="email-assoc-dim"> (auto)</span>}
            </>
          ) : (
            <span className="email-assoc-dim">Not linked to a deal</span>
          )}
        </span>

        {!picking && (
          <span className="email-assoc-actions">
            <button
              className="email-assoc-btn"
              onClick={() => setPicking(true)}
              disabled={busy}
            >
              {linked ? "Change…" : "Link to a deal…"}
            </button>
            {isManual && (
              <button
                className="email-assoc-btn email-assoc-btn-remove"
                onClick={() => void unlink()}
                disabled={busy}
                title="Remove the manual link (reverts to auto-matching)"
              >
                Remove
              </button>
            )}
          </span>
        )}
      </div>

      {picking && (
        <div className="email-assoc-picker">
          <div className="email-assoc-picker-top">
            <input
              className="email-assoc-search"
              placeholder="Search deals by borrower / city…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <button
              className="email-assoc-btn"
              onClick={() => {
                setPicking(false);
                setQuery("");
                setHits([]);
              }}
            >
              Cancel
            </button>
          </div>
          <label className="email-assoc-closed">
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
            />
            Include closed / funded deals
            <span className="email-assoc-dim"> (on by default)</span>
          </label>
          <div className="email-assoc-results">
            {searching && <div className="email-assoc-dim">Searching…</div>}
            {!searching && query.trim().length >= 2 && hits.length === 0 && (
              <div className="email-assoc-dim">No deals match.</div>
            )}
            {hits.map((h) => (
              <button
                key={h.id}
                className="email-assoc-hit"
                onClick={() => void pin(h)}
                disabled={busy}
              >
                <span className="email-assoc-hit-name">{h.name}</span>
                <span
                  className={
                    h.closed ? "email-assoc-hit-closed" : "email-assoc-hit-stage"
                  }
                >
                  {h.stage}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <div className="email-assoc-error">⚠ {error}</div>}
    </div>
  );
}
